import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import AddressInput from "./form/AddressInput";
import VehicleTypeInput from "./form/VehicleTypeInput";
import SecurityTypeInput from "./form/SecurityTypeInput";
import PriceInput from "./form/PriceInput";
import ImageUpload from "./form/ImageUpload";

interface EditFormProps {
  spot: {
    id: string;
    address: string;
    vehicle_type: string;
    security_type: string;
    price_per_hour: number;
    images: string[];
  };
  onCancel: () => void;
  onSuccess: () => void;
  onDelete: () => void;
}

const EditParkingSpotForm = ({ spot, onCancel, onSuccess, onDelete }: EditFormProps) => {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editForm, setEditForm] = useState({
    address: spot.address,
    vehicle_type: spot.vehicle_type,
    security_type: spot.security_type,
    price_per_hour: spot.price_per_hour,
    images: spot.images || [],
    latitude: null as number | null,
    longitude: null as number | null,
  });

  const handleEditSubmit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('parking_spots')
        .update({ 
          address: editForm.address,
          vehicle_type: editForm.vehicle_type,
          security_type: editForm.security_type,
          price_per_hour: editForm.price_per_hour,
          images: editForm.images,
          ...(editForm.latitude !== null && editForm.longitude !== null
            ? { latitude: editForm.latitude, longitude: editForm.longitude }
            : {}),
        })
        .eq('id', spot.id);

      if (error) throw error;
      
      // Force a refresh of the data by calling onSuccess
      onSuccess();
      
      toast({
        title: "Success",
        description: "Parking spot updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this parking spot? This action cannot be undone.")) {
      setIsDeleting(true);
      try {
        const { error } = await supabase
          .from('parking_spots')
          .delete()
          .eq('id', spot.id);

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Parking spot deleted successfully",
        });
        
        onDelete();
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCancel();
  };

  const handleImagesUploaded = (newImagePaths: string[]) => {
    // Replace existing images instead of appending
    setEditForm(prev => ({
      ...prev,
      images: newImagePaths
    }));
  };

  return (
    <Card onClick={(e) => e.stopPropagation()}>
      <CardHeader>
        <CardTitle className="text-xl">Edit Parking Spot</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <AddressInput
            value={editForm.address}
            onChange={(value, coords) =>
              setEditForm(prev => ({
                ...prev,
                address: value,
                latitude: coords ? coords.lat : prev.latitude,
                longitude: coords ? coords.lng : prev.longitude,
              }))
            }
          />

          <VehicleTypeInput
            value={editForm.vehicle_type}
            onChange={(value) => setEditForm(prev => ({ ...prev, vehicle_type: value }))}
          />

          <SecurityTypeInput
            value={editForm.security_type}
            onChange={(value) => setEditForm(prev => ({ ...prev, security_type: value }))}
          />

          <PriceInput
            value={editForm.price_per_hour}
            onChange={(value) => setEditForm(prev => ({ ...prev, price_per_hour: value }))}
          />

          <div className="space-y-2">
            {editForm.images.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium mb-2">Current Images:</h3>
                <div className="grid grid-cols-2 gap-2">
                  {editForm.images.map((imagePath, index) => (
                    <img
                      key={index}
                      src={`${supabase.storage
                        .from("parking-spots")
                        .getPublicUrl(imagePath).data.publicUrl}`}
                      alt={`Parking spot ${index + 1}`}
                      className="w-full h-32 object-cover rounded-md"
                    />
                  ))}
                </div>
              </div>
            )}
            <ImageUpload onImagesUploaded={handleImagesUploaded} />
          </div>

          <div className="flex space-x-2">
            <Button onClick={handleEditSubmit} disabled={isUpdating}>
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteClick}
              disabled={isDeleting}
            >
              Delete Listing
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EditParkingSpotForm;